import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  ClockIcon, 
  CogIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import io from 'socket.io-client';
import { useTheme } from '../contexts/ThemeContext';
import { getApiUrl, getSocketUrl } from '../utils/api';

interface TimerState {
  isRunning: boolean;
  nextExecution: number; // seconds until next execution
  interval: number; // execution interval in seconds
  lastExecuted: Date | null;
}

// localStorage에서 타이머 상태 로드
const loadTimerStateFromStorage = (): TimerState => {
  try {
    const saved = localStorage.getItem('xTrader_timerState');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        isRunning: parsed.isRunning || true,
        nextExecution: parsed.nextExecution || 900,
        interval: parsed.interval || 900,
        lastExecuted: parsed.lastExecuted ? new Date(parsed.lastExecuted) : new Date(Date.now() - 120000)
      };
    }
  } catch (error) {
    console.error('로컬 스토리지에서 타이머 상태 로드 실패:', error);
  }
  
  // 기본값 반환
  return {
    isRunning: true,
    nextExecution: 900,
    interval: 900,
    lastExecuted: new Date(Date.now() - 120000)
  };
};

// localStorage에 타이머 상태 저장
const saveTimerStateToStorage = (state: TimerState) => {
  try {
    localStorage.setItem('xTrader_timerState', JSON.stringify({
      ...state,
      lastExecuted: state.lastExecuted?.toISOString()
    }));
  } catch (error) {
    console.error('로컬 스토리지에 타이머 상태 저장 실패:', error);
  }
};

const BotTimer: React.FC = () => {
  const [timerState, setTimerState] = useState<TimerState>(loadTimerStateFromStorage());

  const [isEditing, setIsEditing] = useState(false);
  const [newInterval, setNewInterval] = useState(900);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);

  // 시계 크기 동적 조정을 위한 ref
  const clockContainerRef = useRef<HTMLDivElement>(null);
  const clockDisplayRef = useRef<HTMLDivElement>(null);

  // 타이머 상태가 변경될 때마다 로컬 스토리지에 저장
  useEffect(() => {
    saveTimerStateToStorage(timerState);
  }, [timerState]);

  // localStorage 저장 함수를 useCallback으로 최적화
  const updateTimerState = useCallback((updateFn: (prev: TimerState) => TimerState) => {
    setTimerState(prev => {
      const newState = updateFn(prev);
      saveTimerStateToStorage(newState);
      return newState;
    });
  }, []);

  useEffect(() => {
    // 서버에서 실제 타이머 상태 가져오기
    const fetchServerTimerStatus = async () => {
      try {
        const response = await fetch(getApiUrl() + '/api/timer-status');
        if (response.ok) {
          const serverTimer = await response.json();
          updateTimerState(prev => ({
            ...prev,
            isRunning: serverTimer.isRunning,
            nextExecution: serverTimer.nextExecution,
            lastExecuted: serverTimer.lastExecuted ? new Date(serverTimer.lastExecuted) : null
          }));
        }
      } catch (error) {
        console.error('서버 타이머 상태 가져오기 실패:', error);
      }
    };

    // 서버 설정 가져오기
    const fetchServerSettings = async () => {
      try {
        const response = await fetch(getApiUrl() + '/api/settings');
        if (response.ok) {
          const settings = await response.json();
          const actualInterval = settings.aiTimerInterval || 900;
          
          updateTimerState(prev => ({
            ...prev,
            interval: actualInterval
          }));
          setNewInterval(actualInterval);
        }
      } catch (error) {
        console.error('설정 가져오기 실패:', error);
      }
    };

    // WebSocket 연결 설정
    let socket: any = null;
    
    try {
      const serverUrl = getSocketUrl();
      
      console.log('🔗 봇 타이머 서버 연결 시도:', serverUrl);
      
      socket = io(serverUrl, {
        transports: ['websocket', 'polling'],
        timeout: 10000,
        forceNew: true
      });

      socket.on('connect', () => {
        console.log('✅ 봇 타이머 소켓 연결됨');
        setIsWebSocketConnected(true);
      });

      socket.on('disconnect', () => {
        console.log('❌ 봇 타이머 소켓 연결 해제됨');
        setIsWebSocketConnected(false);
      });

      // 봇 타이머 업데이트 수신
      socket.on('botTimerUpdate', (data: any) => {
        console.log('⏰ 봇 타이머 업데이트 수신:', data);
        updateTimerState(prev => ({
          ...prev,
          isRunning: data.isRunning,
          nextExecution: data.nextExecution,
          lastExecuted: data.lastExecuted ? new Date(data.lastExecuted) : prev.lastExecuted
        }));
      });

      // 봇 상태 업데이트 수신 (완전 초기화)
      socket.on('botStatusUpdate', (data: any) => {
        console.log('🤖 봇 타이머 - 봇 상태 업데이트 수신:', data);
        if (data.isRunning !== undefined) {
          updateTimerState(prev => ({
            ...prev,
            isRunning: data.isRunning,
            // 봇이 시작될 때 타이머 완전 초기화
            nextExecution: data.isRunning && data.nextExecution ? data.nextExecution : (data.isRunning ? prev.interval : prev.nextExecution),
            lastExecuted: data.lastExecuted ? new Date(data.lastExecuted) : (data.isRunning ? null : prev.lastExecuted)
          }));
        }
      });

      socket.on('connect_error', (error: any) => {
        console.error('❌ 봇 타이머 소켓 연결 오류:', error);
        setIsWebSocketConnected(false);
      });

    } catch (error) {
      console.error('❌ 봇 타이머 소켓 초기화 오류:', error);
    }

    // 초기 데이터 로드
    fetchServerTimerStatus();
    fetchServerSettings();

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [updateTimerState]);

  // 로컬 타이머 카운트다운 (WebSocket이 연결되지 않았을 때만 사용)
  useEffect(() => {
    let timer: NodeJS.Timeout;

    // WebSocket이 연결되지 않았을 때만 로컬 타이머 실행
    if (!isWebSocketConnected && timerState.isRunning && timerState.nextExecution > 0) {
      timer = setInterval(() => {
        setTimerState(prev => {
          if (prev.nextExecution <= 1) {
            // 타이머가 0에 도달하면 리셋
            return {
              ...prev,
              nextExecution: prev.interval,
              lastExecuted: new Date()
            };
          }
          return {
            ...prev,
            nextExecution: prev.nextExecution - 1
          };
        });
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isWebSocketConnected, timerState.isRunning, timerState.nextExecution, timerState.interval]);

  // 시계 크기 동적 조정 - 간단하고 확실한 방법
  useEffect(() => {
    const adjustClockSize = () => {
      if (clockDisplayRef.current && clockContainerRef.current) {
        const container = clockContainerRef.current;
        const clockElement = clockDisplayRef.current;
        
        // 컨테이너 크기 측정
        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;
        
        console.log('🕐 컨테이너 크기:', { containerWidth, containerHeight });
        
        // 시계 크기를 컨테이너 크기에 맞춰 계산
        // 가로 크기의 25%를 기본으로 하되, 최소 40px, 최대 120px
        let fontSize = Math.max(40, Math.min(120, containerWidth * 0.25));
        
        // 세로 크기도 고려하여 조정
        const heightBasedSize = Math.max(40, Math.min(120, containerHeight * 0.3));
        fontSize = Math.min(fontSize, heightBasedSize);
        
        // 픽셀 단위로 직접 설정
        clockElement.style.fontSize = `${fontSize}px`;
        clockElement.style.lineHeight = '1.1';
        
        console.log('🕐 설정된 폰트 크기:', fontSize + 'px');
      }
    };

    // 초기 크기 조정 (약간의 지연을 두어 DOM이 완전히 렌더링된 후 실행)
    const timeoutId = setTimeout(adjustClockSize, 200);

    // 윈도우 리사이즈 이벤트 리스너
    const handleResize = () => {
      setTimeout(adjustClockSize, 100);
    };
    
    window.addEventListener('resize', handleResize);

    // ResizeObserver로 컨테이너 크기 변화 감지
    let resizeObserver: ResizeObserver | null = null;
    if (clockContainerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        setTimeout(adjustClockSize, 50);
      });
      resizeObserver.observe(clockContainerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatInterval = (seconds: number): string => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}분`;
    }
    return `${seconds}초`;
  };

  const updateInterval = async () => {
    try {
      const response = await fetch(getApiUrl() + '/api/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          aiTimerInterval: newInterval
        }),
      });

      if (response.ok) {
        setTimerState(prev => ({
          ...prev,
          interval: newInterval,
          nextExecution: newInterval
        }));
        setIsEditing(false);
      } else {
        alert('설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('설정 업데이트 오류:', error);
      alert('설정 업데이트 중 오류가 발생했습니다.');
    }
  };

  const getStatus = () => {
    if (!timerState.isRunning) return '정지됨';
    if (timerState.nextExecution <= 0) return '분석 중';
    return '대기 중';
  };

  const getStatusColorClass = () => {
    if (!timerState.isRunning) return 'text-red-600 dark:text-red-400';
    if (timerState.nextExecution <= 0) return 'text-blue-600 dark:text-blue-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getStatusBgClass = () => {
    if (!timerState.isRunning) return 'bg-red-500';
    if (timerState.nextExecution <= 0) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getProgressBarColor = () => {
    if (timerState.nextExecution < 30) return 'bg-gradient-to-r from-red-500 to-red-600';
    if (timerState.nextExecution < 60) return 'bg-gradient-to-r from-orange-500 to-yellow-500';
    return 'bg-gradient-to-r from-green-500 to-blue-500';
  };

  const progressPercentage = (timerState.nextExecution / timerState.interval) * 100;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center" 
            style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
          <div className="relative mr-3">
            <ClockIcon className="h-7 w-7 text-blue-600 dark:text-blue-400" />
            {timerState.isRunning && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg"></div>
            )}
          </div>
          🤖 AI 봇 타이머
        </h3>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-all duration-200"
        >
          <CogIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 space-y-5">
        {/* Main Timer Display */}
        <div ref={clockContainerRef} className="relative bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-800 dark:via-gray-800 dark:to-blue-900/20 rounded-3xl p-3 sm:p-4 md:p-5 lg:p-6 border border-slate-200 dark:border-gray-600 shadow-sm overflow-hidden min-h-fit">
          {/* 엄청 큰 시계 */}
          <div className="text-center mb-3 sm:mb-4 md:mb-5">
            <div className="relative">
              {/* 배경 글로우 효과 */}
              <div className="absolute inset-0 blur-3xl opacity-30">
                <div className="font-black bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent"
                     style={{ 
                       fontSize: '80px', // 기본값을 크게 설정
                       lineHeight: '1.1'
                     }}>
                  {formatTime(timerState.nextExecution)}
                </div>
              </div>
              
              {/* 메인 시계 */}
              <div ref={clockDisplayRef}
                   className="relative font-black bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-500 bg-clip-text text-transparent font-sans tracking-wider mb-2 sm:mb-3 md:mb-4 drop-shadow-2xl" 
                   style={{ 
                     fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                     letterSpacing: '0.05em',
                     fontVariantNumeric: 'tabular-nums',
                     fontSize: '80px', // 기본값을 크게 설정
                     lineHeight: '1.1'
                   }}>
                {formatTime(timerState.nextExecution)}
              </div>
              
              {/* 시계 아래 작은 점들 */}
              <div className="flex justify-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                <div className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full animate-pulse delay-150"></div>
                <div className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full animate-pulse delay-300"></div>
              </div>
            </div>
            
            <div className="text-base sm:text-lg md:text-xl text-gray-500 dark:text-gray-400 font-medium"
                 style={{ fontFamily: '"SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
              남은 시간
            </div>
          </div>

          {/* 프로그래스바 */}
          <div className="relative w-full bg-slate-200 dark:bg-gray-700 rounded-full h-3 sm:h-4 md:h-5 mb-3 sm:mb-4 md:mb-5 overflow-hidden">
            {/* 배경 글로우 */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 opacity-20 blur-sm"></div>
            
            <div 
              className={`relative h-3 sm:h-4 md:h-5 rounded-full transition-all duration-1000 ease-out ${getProgressBarColor()} shadow-lg`}
              style={{ 
                width: `${progressPercentage}%`,
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)'
              }}
            />
          </div>

          {/* Status and Info */}
          <div className="text-center space-y-3 sm:space-y-4">
            <div className="flex items-center justify-center space-x-3 sm:space-x-4">
              <div className={`w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded-full animate-pulse ${getStatusBgClass()} shadow-lg`} 
                   style={{ boxShadow: `0 0 15px ${timerState.isRunning ? '#22c55e' : '#ef4444'}` }} />
              <span className={`text-lg sm:text-xl md:text-2xl font-bold ${getStatusColorClass()}`}
                    style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                {getStatus()}
              </span>
            </div>
            <div className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400"
                 style={{ fontFamily: '"SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
              실행 주기: <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{formatInterval(timerState.interval)}</span>
            </div>
          </div>
        </div>

        {/* 마지막 실행 정보 */}
        <div className="bg-white dark:bg-gray-700 rounded-2xl p-5 border border-slate-200 dark:border-gray-600 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center"
                  style={{ fontFamily: '"SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
              <ChartBarIcon className="h-5 w-5 mr-3 text-blue-500" />
              마지막 분석
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-white"
                  style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
              {timerState.lastExecuted ? (
                (() => {
                  try {
                    const date = timerState.lastExecuted instanceof Date 
                      ? timerState.lastExecuted 
                      : new Date(timerState.lastExecuted);
                    const now = new Date();
                    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
                    
                    if (diffMinutes < 1) return '방금 전';
                    if (diffMinutes < 60) return `${diffMinutes}분 전`;
                    const diffHours = Math.floor(diffMinutes / 60);
                    return `${diffHours}시간 전`;
                  } catch (error) {
                    return '정보 없음';
                  }
                })()
              ) : '정보 없음'}
            </span>
          </div>
        </div>

        {/* Interval Settings */}
        {isEditing && (
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-5 space-y-4 border-2 border-dashed border-slate-300 dark:border-gray-600">
            <div className="flex items-center space-x-3 text-sm text-gray-700 dark:text-gray-300 font-semibold"
                 style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
              <span>실행 주기 설정</span>
            </div>
            <div className="space-y-3">
              <select
                value={newInterval}
                onChange={(e) => setNewInterval(Number(e.target.value))}
                className="w-full bg-white dark:bg-gray-600 border-2 border-slate-300 dark:border-gray-500 text-gray-900 dark:text-white text-sm rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                style={{ fontFamily: '"SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}
              >
                <option value={60}>1분 (테스트용)</option>
                <option value={180}>3분 (빠른 분석)</option>
                <option value={300}>5분 (기본 권장)</option>
                <option value={600}>10분 (안정적)</option>
                <option value={900}>15분 (신중함)</option>
                <option value={1800}>30분 (장기)</option>
                <option value={3600}>1시간 (보수적)</option>
              </select>
              <button
                onClick={updateInterval}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm py-3 px-4 rounded-xl transition-all duration-200 font-semibold shadow-md hover:shadow-lg"
                style={{ fontFamily: '"SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}
              >
                주기 업데이트
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BotTimer; 